import { Badge, Button, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { MessageKey } from "../../shared/i18n";
import { TEMPLATES } from "../../shared/templates";
import { useAuth } from "../auth/provider";
import { LanguageSwitcher } from "../components/language-switcher";
import { Logo } from "../components/logo";
import { TemplatePreview } from "../components/template-preview";
import { TemplateThumb } from "../components/template-thumb";
import { ThemeToggle } from "../components/theme-toggle";
import { sampleDocument, withTemplate } from "../cv/sample";
import { useLocale, useMessages } from "../i18n/provider";
import styles from "./landing.module.css";

export function LandingPage() {
  const t = useMessages();
  const { locale } = useLocale();
  const { isAuthenticated } = useAuth();
  const [previewId, setPreviewId] = useState<string | null>(null);

  const samples = useMemo(
    () =>
      TEMPLATES.map((meta) => ({
        meta,
        doc: withTemplate(
          sampleDocument(locale),
          meta.id,
          meta.accent,
          meta.fontPair,
          meta.density,
        ),
      })),
    [locale],
  );

  const hero = samples[1];
  const strip = samples.slice(0, 6);
  const previewIndex = samples.findIndex((item) => item.meta.id === previewId);
  const preview = previewIndex >= 0 ? samples[previewIndex] : null;

  const cycle = (delta: number) => {
    const next = (previewIndex + delta + samples.length) % samples.length;
    setPreviewId(samples[next]?.meta.id ?? null);
  };

  const createTo = isAuthenticated ? "/dashboard" : "/signup";

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Logo />
          <Group gap={8}>
            <ThemeToggle />
            {isAuthenticated ? (
              <Button component={Link} to="/dashboard" size="sm">
                {t("nav.dashboard")}
              </Button>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/login"
                  variant="subtle"
                  color="gray"
                  size="sm"
                  visibleFrom="xs"
                >
                  {t("nav.signIn")}
                </Button>
                <Button component={Link} to="/signup" size="sm">
                  {t("landing.cta")}
                </Button>
              </>
            )}
          </Group>
        </div>
      </header>

      <main>
        <section className={styles.hero}>
          <div className={styles.heroCopy}>
            <h1 className={styles.heroTitle}>{t("landing.heroTitle")}</h1>
            <p className={styles.heroSubtitle}>{t("landing.heroSubtitle")}</p>
            <Group gap={12} mt={8}>
              <Button
                component={Link}
                to={createTo}
                size="md"
                rightSection={<IconArrowRight size={17} stroke={1.5} />}
              >
                {t("landing.cta")}
              </Button>
              <Button
                component={Link}
                to={isAuthenticated ? "/templates" : "/login"}
                variant="subtle"
                color="gray"
                size="md"
              >
                {t("landing.secondary")}
              </Button>
            </Group>
          </div>
          <div className={styles.heroVisual}>
            {hero ? (
              <div className={styles.heroPage}>
                <TemplateThumb doc={hero.doc} />
              </div>
            ) : null}
          </div>
        </section>

        <section className={styles.proof} aria-label={t("landing.proofTemplates")}>
          <div className={styles.proofRow}>
            <span className={styles.proofNumber}>08</span>
            <span>{t("landing.proofTemplates")}</span>
          </div>
          <div className={styles.proofRow}>
            <span className={styles.proofNumber}>01</span>
            <span>{t("landing.proofPrivacy")}</span>
          </div>
          <div className={styles.proofRow}>
            <span className={styles.proofNumber}>02</span>
            <span>{t("landing.proofExport")}</span>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>{t("landing.templatesTitle")}</h2>
            <Button
              component={Link}
              to={isAuthenticated ? "/templates" : "/login"}
              variant="subtle"
              color="gray"
              size="sm"
              rightSection={<IconArrowRight size={16} stroke={1.5} />}
            >
              {t("landing.viewAll")}
            </Button>
          </div>
          <div className={styles.strip}>
            {strip.map(({ meta, doc }) => (
              <button
                key={meta.id}
                type="button"
                className={styles.stripItem}
                onClick={() => setPreviewId(meta.id)}
                aria-haspopup="dialog"
              >
                <TemplateThumb doc={doc} />
                <span className={styles.stripName}>{t(meta.nameKey as MessageKey)}</span>
              </button>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("landing.stepsTitle")}</h2>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={24} mt="lg">
            {[t("landing.step1"), t("landing.step2"), t("landing.step3")].map((step, index) => (
              <div key={step} className={styles.step}>
                <span className={styles.stepNumber}>0{index + 1}</span>
                <p className={styles.stepText}>{step}</p>
              </div>
            ))}
          </SimpleGrid>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("landing.pricingTitle")}</h2>
          <p className={styles.sectionSubtitle}>{t("landing.pricingSubtitle")}</p>
          <div className={styles.pricing}>
            <div className={styles.priceCard}>
              <Group justify="space-between">
                <Text fw={600}>{t("common.free")}</Text>
                <Text fw={600} size="lg">
                  {t("landing.freePrice")}
                </Text>
              </Group>
              <Stack gap={10} mt="lg">
                {[
                  "landing.freeFeature1",
                  "landing.freeFeature2",
                  "landing.freeFeature3",
                  "landing.freeFeature4",
                ].map((key) => (
                  <Text key={key} size="sm" c="dimmed">
                    {t(key as MessageKey)}
                  </Text>
                ))}
              </Stack>
              <Button component={Link} to={createTo} variant="light" fullWidth mt="xl">
                {t("landing.cta")}
              </Button>
            </div>
            <div className={`${styles.priceCard} ${styles.priceCardPro}`}>
              <Group justify="space-between">
                <Group gap={8}>
                  <Text fw={600}>{t("common.pro")}</Text>
                  <Badge variant="light">{t("common.savePercent")}</Badge>
                </Group>
                <Text fw={600} size="lg">
                  {t("landing.proPrice")}
                </Text>
              </Group>
              <Stack gap={10} mt="lg">
                {[
                  "landing.proFeature1",
                  "landing.proFeature2",
                  "landing.proFeature3",
                  "landing.proFeature4",
                ].map((key) => (
                  <Text key={key} size="sm" c="dimmed">
                    {t(key as MessageKey)}
                  </Text>
                ))}
              </Stack>
              <Button
                component={Link}
                to={isAuthenticated ? "/billing" : "/signup"}
                fullWidth
                mt="xl"
              >
                {t("common.upgrade")}
              </Button>
            </div>
          </div>
        </section>
      </main>

      {preview ? (
        <TemplatePreview
          template={preview.meta}
          doc={preview.doc}
          useHref={createTo}
          onClose={() => setPreviewId(null)}
          onPrevious={() => cycle(-1)}
          onNext={() => cycle(1)}
        />
      ) : null}

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div>
            <Logo />
            <p className={styles.footerNote}>{t("landing.footerNote")}</p>
          </div>
          <Group gap={20} align="center">
            <Link className={styles.footerLink} to="/privacy">
              {t("legal.privacy")}
            </Link>
            <Link className={styles.footerLink} to="/terms">
              {t("legal.terms")}
            </Link>
            <Link className={styles.footerLink} to="/refunds">
              {t("legal.refunds")}
            </Link>
            <LanguageSwitcher />
          </Group>
        </div>
      </footer>
    </div>
  );
}
